import ConversationDetails from 'components/Conversation/ConversationDetails';
import React, { useEffect, useState, useMemo } from 'react';
import TransferConversationModal from './components/notificationModal/transferModal';
import WhisperModal from './components/notificationModal/whisperModal';

import 'react-virtualized/styles.css';

import { verifyRoles } from 'components/Authentication/VerifyRoles';
import { IconsChannel } from 'components/IconsChannel/IconsChannel';
import { TagChatClient } from 'components/TagChatClient';
import { PhoneCell } from 'components/FlexTable/components/cells/phone';
import { toast } from 'react-toastify';
import ConversationReportService from 'views/ChatConfiguration/service';

import GetConversationService from '../../../conversation/services/apiConversation';
import { individual_column_style } from '../../../../../../assets/styles/table_custom_style';
import { ChatSituations } from '../../../../../../utils/fuctions/mapSituationChat';
import CloseSingleButton from '../CloseSingleButton';
import { FlexTableV2 } from 'components/FlexTableV2';
import { createColumnHelper, getSortedRowModel } from '@tanstack/react-table';
import { LOCAL_STORAGE_KEYS } from 'utils/fuctions/localStorage';
import { MemoizedAgentProfilePicture } from 'components/AgentProfilePictureByLogin/MemoizedAgentProfilePicture';

const columnHelper = createColumnHelper();

/**
 * @typedef NameTag
 * @property {id} id
 * @property {string} name
 * @property {boolean} disabled
 * @property {string} color - Uma cor em formato HEX, incluindo #
 */

/**
 * Representa um cliente.
 * @typedef {Object} Client
 * @property {string} id - O ID do cliente
 * @property {string} nome - O nome do cliente
 * @property {string|null} email - O email do cliente
 * @property {string} telefone - O telefone do cliente
 * @property {string} id_postgres - O ID do cliente no banco de dados Postgres
 * @property {string} id_facebook - O ID do cliente no Facebook
 * @property {string} id_telegram - O ID do cliente no Telegram
 * @property {string} id_whatsapp - O ID do cliente no WhatsApp
 * @property {string|null} id_instagram - O ID do cliente no Instagram
 * @property {string} id_zenvia_sms - O ID do cliente no Zenvia SMS
 * @property {boolean} was_created_automatically - Indica se o cliente foi criado automaticamente
 * @property {number} record_type_id - O ID do tipo de registro do cliente
 * @property {Array|null} broadcastList - A lista de transmissão do cliente
 * @property {Array} tags - As tags do cliente
 * @property {string} created_at - A data de criação do cliente
 * @property {string} last_updated_at - A data da última atualização do cliente
 * @property {string} created_by - Quem criou o cliente
 * @property {string} last_updated_by - Quem atualizou o cliente pela última vez
 * @property {number|null} latitude - A latitude do cliente
 * @property {number|null} longitude - A longitude do cliente
 */

/**
 * Representa um atendente.
 * @typedef {Object} Attendant
 * @property {string} id_postgres - O ID do atendente no banco de dados Postgres
 * @property {string} name - O nome do atendente
 */

/**
 * Representa conversas entre cliente e atendente. É utilizado no atendimento e no monitoramento.
 * @typedef {Object} Conversation
 * @property {string} _id - O ID da conversa
 * @property {string} canal - O canal da conversa (e.g., whatsapp)
 * @property {string} apelidoCanal - O apelido do canal
 * @property {boolean} status - Indica se a conversa foi atendida
 * @property {boolean} atendimentoBot - Indica se o atendimento foi realizado por um bot
 * @property {string} situacao - A situação da conversa (e.g., em_atendimento)
 * @property {string} fila - A fila da conversa (e.g., TELEVENDAS)
 * @property {Client} cliente - Informações do cliente
 * @property {string} hora_criacao - A hora de criação da conversa
 * @property {Attendant} atendente - Informações do atendente
 * @property {string} hora_do_atendimento - A hora do atendimento
 * @property {boolean} isBroadCastList - Indica se é uma lista de transmissão
 * @property {string} nameBroadCastList - O nome da lista de transmissão
 * @property {Array} tags - As tags da conversa
 */

/**
 * Um modal para fechar conversa. É possível escolher entre criar ou não protocolo.
 * @param {Object} props
 * @param {string} props.title - O texto que aparecerá no header do cabeçalho
 * @param {string} props.tooltipTitle - O texto que aparecerá no tooltip antes do modal abrir
 * @param {string} props.chatId - O ID do chat.
 * @param {string} props.reason - O motivo em forma de string
 * @param {string} props.subReason - O sub-motivo em forma de string
 * @param {string} props.idProtocol - O id do protocolo em forma de UUID
 * @param {string} props.recordType - O tipo de registro do protocolo
 * @param {boolean} props.shouldSkipProtocolCreationOnTheBackend - Indica se a criação de protocolo deve ser PULADA ou não. É utilizado puramente no back-end. Não exerce influência alguma no front-end. Para esse uso, utilize `shouldShowProtocolAtTheEndOfConversation`.
 * @param {boolean} props.shouldShowProtocolAtTheEndOfConversation - Indica se um protocolo deve ou não ser criado ao encerrar a conversa
 * @param {Conversation} props.conversation - A conversa associada ao modal
 * @returns {React.ReactElement}
 */

/**
 * Renderiza a tabela do monitoramento de chats
 * @param {Object} props
 * @param {Conversation[]} props.data - Uma lista de conversas
 * @param {boolean} props.isCurrentAgentsLogged
 * @param {Conversation[]} props.selectedRows - A lista de conversas que estão selecionadas
 * @param {NameTag[]} props.idNameTags
 */
const TableMonitoring = ({
  data,
  isCurrentAgentsLogged,
  selectedRows,
  setSelectedRows,
  handleOnSelect,
  handleOnSelectAll,
  handleOnSortChange,
  sorting,
  setSorting,
  idNameTags
}) => {
  /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} - Determina se deve ou não ser criado um protocolo ao encerrar a conversa */

  const localStorageState = (() => {
    try {
      return (
        JSON.parse(
          localStorage.getItem(LOCAL_STORAGE_KEYS.CONVERSATION_REPORT_TABLE)
        ) || {}
      );
    } catch {
      return {};
    }
  })();

  const [columnSizingAgents, setColumnSizingAgents] = useState(
    localStorageState.columnSizing
  );
  const [columnVisibilityAgents, setColumnVisibilityAgents] = useState(
    localStorageState.columnVisibility
  );
  const [columnOrderAgents, setColumnOrderAgents] = useState(
    localStorageState.columnOrder
  );
  const [dataUpdated, setDataUpdated] = useState({});

  useEffect(() => {
    const fetchTimelines = async () => {
      const updatedData = await Promise.all(
        data.map(async (conversation) => {
          try {
            const res = await GetConversationService.get(conversation._id);
            return { ...conversation, timeline: res.data.timeline };
          } catch (err) {
            console.error(
              `Erro ao buscar timeline para ${conversation._id}:`,
              err
            );
            return conversation;
          }
        })
      );
      setDataUpdated(updatedData);
    };

    if (data.length > 0) {
      fetchTimelines();
    }
  }, [data]);

  const agentColumns = useMemo(
    () => [
      ClienteColumn,
      OrigemColumn,
      TagsColumn(idNameTags),
      TelefoneColumn,
      AtendenteColumn,
      StatusColumn,
      FilaColumn,
      NomeCanalColumn,
      CanalColumn,
      DataHoraColumn,
      SituacaoColumn,
      AcoesColumn(isCurrentAgentsLogged)
    ],
    [idNameTags, isCurrentAgentsLogged]
  );

  return (
    <div
      style={{
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '700px',
        minHeight: '400px',
        height:
          data.length > 0 ? `${Math.min(data.length * 50, 900)}px` : '500px'
      }}
    >
      <FlexTableV2
        table={{
          columns: agentColumns,
          data,
          getRowId: (row) => row._id || row.id,
          enableLocalStorageMemoWithKey:
            LOCAL_STORAGE_KEYS.CONVERSATION_REPORT_TABLE,
          columnResizeMode: 'onChange',
          enableLocalStorageMemo: true,
          getSortedRowModel: getSortedRowModel(),
          enableSortingRemoval: false,
          manualSorting: false,
          virtualizerOptions: {
            estimateSize: () => 44,
            overscan: 10
          },
          enableOverflowX: true,
          scrollContainerHeight: 'auto'
        }}
        state={{
          setColumnSizing: setColumnSizingAgents,
          columnVisibility: columnVisibilityAgents,
          setColumnVisibility: setColumnVisibilityAgents,
          setColumnOrder: setColumnOrderAgents,
          columnOrder: columnOrderAgents,
          rowSelection: selectedRows,
          setRowSelection: setSelectedRows,
          sorting,
          setSorting
        }}
      />
    </div>
  );
};
export default TableMonitoring;

const BadgeOrigem = ({ label, color = 'gray' }) => (
  <div
    style={{
      padding: '.125rem .5rem',
      backgroundColor: color,
      borderRadius: '.5rem',
      maxWidth: 'fit-content',
      height: 'fit-content',
      color: 'white',
      marginBlock: 'auto',
      fontSize: '.75rem',
      whiteSpace: 'normal',
      lineHeight: '1.2'
    }}
  >
    {label}
  </div>
);

const ClienteColumn = columnHelper.accessor('cliente.nome', {
  id: 'cliente.nome',
  header: () => <div style={{ padding: '0 15px' }}>Cliente</div>,
  cell: ({ getValue }) => <div style={{ padding: '0 15px' }}>{getValue()}</div>,
  width: 160,
  sortingFn: 'text',
  id: 'cliente'
});

const OrigemColumn = columnHelper.accessor('origem', {
  id: 'origem',
  header: () => <div style={{ padding: '0 15px' }}>Origem</div>,
  cell: ({ row }) => {
    const content = row.original?.isBroadCastList ? (
      <BadgeOrigem
        label={
          <>
            Lista de transmissão -<br />
            {row.original.nameBroadCastList}
          </>
        }
        color="#0ea5e9"
      />
    ) : row.original?.isContatoAtivo ? (
      <BadgeOrigem label="Ativo" color="#0ea5e9" />
    ) : (
      <BadgeOrigem label="Receptivo" color="#1e40af" />
    );
    return <div style={{ padding: '0 8px' }}>{content}</div>;
  },
  width: 160,
  sortingFn: (a, b) => {
    const origemA = a.original?.isBroadCastList
      ? 2
      : a.original?.isContatoAtivo
      ? 1
      : 0;
    const origemB = b.original?.isBroadCastList
      ? 2
      : b.original?.isContatoAtivo
      ? 1
      : 0;
    return origemA - origemB;
  }
});

const TagsColumn = (idNameTags) =>
  columnHelper.accessor('tags', {
    id: 'tags',
    header: () => <div style={{ padding: '0 15px' }}>Tags</div>,
    cell: ({ row }) => {
      const countTags = row.original?.tags?.length;
      if (row.original?.tags && row.original.tags.length > 2)
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '0 8px'
            }}
          >
            <TagChatClient
              name={idNameTags[row.original.tags[0]]?.name}
              color={idNameTags[row.original.tags[0]]?.color || '#5086c1'}
              disabled={idNameTags[row.original.tags[0]]?.disabled}
            />
            <TagChatClient
              name={idNameTags[row.original.tags[2]]?.name}
              color={idNameTags[row.original.tags[2]]?.color || '#5086c1'}
              disabled={idNameTags[row.original.tags[1]]?.disabled}
            />

            <TagChatClient
              name={`+${countTags - 2}`}
              allTags={row.original?.tags}
              idNameTags={idNameTags}
              isMore={true}
              color="#5086c1"
              index={row.original?.id || row.original?._id}
            />
          </div>
        );
      else if (row.original?.tags && row.original?.tags.length > 0) {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '0 8px'
            }}
          >
            {Object.keys(idNameTags).length > 0 &&
              row.original?.tags &&
              row.original?.tags?.map((tag, index) => (
                <TagChatClient
                  key={index}
                  name={idNameTags[tag]?.name}
                  color={idNameTags[tag]?.color || '#5086c1'}
                  disabled={idNameTags[tag]?.disabled}
                  sizeText="auto"
                />
              ))}
          </div>
        );
      }
      return null;
    },
    width: 180
  });

const TelefoneColumn = columnHelper.accessor('cliente.telefone', {
  id: 'cliente.telefone',
  header: () => <div style={{ padding: '0 8px' }}>Telefone</div>,
  cell: ({ row }) => {
    return (
      <div style={{ padding: '0 8px' }}>
        <PhoneCell
          phone={row.original?.cliente?.telefone}
          ddi={row.original?.cliente?.ddi || '55'}
        />
      </div>
    );
  },
  width: 180,
  id: 'telefone'
});

const AtendenteColumn = columnHelper.accessor('atendente.name', {
  id: 'atendente.name',
  header: () => <div style={{ padding: '0 8px' }}>Atendente</div>,
  cell: ({ row }) => {
    if (!row.original?.atendente?.id_postgres) return null;

    return (
      <div
        title={row.original.atendente?.name}
        style={{ display: 'flex', alignItems: 'left', padding: '0 8px' }}
      >
        <MemoizedAgentProfilePicture
          login={row.original?.atendente?.id_postgres}
          alt="profile"
          style={{
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            boxShadow: '0 0 0 1px #00000010'
          }}
        />
        <div
          className="ml-2 p-0"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
        >
          <label className="m-0 p-0" style={{ textAlign: 'left' }}>
            {row.original?.atendente?.name}
          </label>
        </div>
      </div>
    );
  },
  width: 150,
  sortingFn: 'text',
  id: 'atendente'
});

const StatusColumn = columnHelper.accessor('atendida', {
  id: 'atendida',
  header: () => <div style={{ padding: '0 8px' }}>Status</div>,
  cell: ({ row }) => (
    <div style={{ padding: '0 8px' }}>
      {row.original?.atendida ? 'Atendida' : 'Não Atendida'}
    </div>
  ),
  width: 150
});

const FilaColumn = columnHelper.accessor('fila', {
  id: 'fila',
  header: () => <div style={{ padding: '0 8px' }}>Fila</div>,
  cell: ({ row }) => {
    return <div style={{ padding: '0 8px' }}>{row.original?.fila}</div>;
  },
  width: 150,
  sortingFn: 'text'
});

const NomeCanalColumn = columnHelper.accessor('apelidoCanal', {
  id: 'apelidoCanal',
  header: () => <div style={{ padding: '0 8px' }}>Nome do Canal</div>,
  cell: ({ row }) => {
    return (
      <div style={{ padding: '0 8px' }}>
        {row.original?.apelidoCanal || null}
      </div>
    );
  },
  width: 150,
  sortingFn: 'text'
});

const CanalColumn = columnHelper.accessor('canal', {
  id: 'canal',
  header: () => <div style={{ padding: '0 8px' }}>Canal</div>,
  cell: ({ row }) => {
    return (
      <div style={{ padding: '0 8px' }}>
        <IconsChannel channel={row.original.canal} />
      </div>
    );
  },
  width: 180,
  sortingFn: 'text'
});

const DataHoraColumn = columnHelper.accessor('hora_criacao', {
  id: 'hora_criacao',
  header: () => <div style={{ padding: '0 8px' }}>Data e Hora</div>,
  cell: ({ row }) => {
    const date = new Date(row.original?.hora_criacao);
    const dateString =
      date.toLocaleDateString('pt-br') === 'Invalid Date'
        ? ''
        : `${date.toLocaleDateString('pt-br')} ${date.toLocaleTimeString(
            'pt-br'
          )}`;
    return <div style={{ padding: '0 8px' }}>{dateString}</div>;
  },
  width: 150
});

const SituacaoColumn = columnHelper.accessor('situacao', {
  id: 'situacao',
  header: () => <div style={{ padding: '0 8px' }}>Situação</div>,
  cell: ({ row }) => {
    let situacao;
    switch (row.original?.situacao) {
      case ChatSituations.IN_ATTENDANCE:
        situacao = 'Em atendimento';
        break;
      case ChatSituations.NOT_ANSWERED:
        situacao = 'Não atendida';
        break;
      case ChatSituations.TRANSFERRED:
        situacao = 'Transferida';
        break;
      case ChatSituations.WAITING_IN_QUEUE:
        situacao = 'Aguardando fila';
        break;
      case ChatSituations.ASSIGNED:
        situacao = 'Atribuída';
        break;
      case ChatSituations.ABANDONED:
        situacao = 'Abandonada';
        break;
      default:
        situacao = 'Tipo inválido';
    }
    return <div style={{ padding: '0 8px' }}>{situacao}</div>;
  },
  width: 150
});

const AcoesColumn = (isCurrentAgentsLogged) =>
  columnHelper.display({
    id: 'acoes',
    header: () => <div style={{ paddingLeft: '55px' }}>Ações</div>,
    minSize: 180,
    cell: ({ row }) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <ConversationDetails
          conversationData={row.original}
          getDataOnModalOpen={true}
          id={row.original._id ?? row.original.id}
        />
        {verifyRoles(['CHAT_TRANSFERS']) && (
          <TransferConversationModal
            id={row.original._id}
            name="Transferir conversa"
            tooltipTitle="Transferir conversa"
            conversation={row.original}
            isCurrentAgentsLogged={isCurrentAgentsLogged}
          />
        )}
        {verifyRoles(['WHISPER_TOOL']) && (
          <WhisperModal
            id={row.original._id}
            name="Sussurrar conversa"
            tooltipTitle="Sussurrar conversa"
          />
        )}
        {verifyRoles(['ENDING_CONVERSATIONS']) && (
          <CloseSingleButton conversation={row.original} />
        )}
      </div>
    ),
    width: 160
  });
